import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { getSyncedNow } from "@/lib/clockSync";

export function useCountdown(
  targetTimestamp: number,
  doneText?: string,
  isTrenches?: boolean,
  onComplete?: () => void,
  format: "default" | "daysHoursSeconds" | "compact" | "compactNoSeconds" = "default",
) {
  const [timeLeft, setTimeLeft] = useState("");
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    // Reset completion flag when targetTimestamp changes
    hasCompletedRef.current = false;
  }, [targetTimestamp]);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = dayjs(getSyncedNow());
      const target = dayjs.unix(targetTimestamp);
      const diff = target.diff(now, "second");

      if (diff <= 0) {
        setTimeLeft(doneText ? doneText : isTrenches ? "00:00" : "00:00:00");
        // Call onComplete callback only once when countdown reaches zero
        if (!hasCompletedRef.current && onComplete) {
          hasCompletedRef.current = true;
          onComplete();
        }
        return;
      }
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      if (isTrenches) {
        // 🚀 format MM:SS for trenches
        const totalMinutes = hours * 60 + minutes;
        setTimeLeft(
          `${totalMinutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`,
        );
      } else if (format === "daysHoursSeconds") {
        const days = Math.floor(diff / 86400);
        const remainingHours = Math.floor((diff % 86400) / 3600);
        const remainingMinutes = Math.floor((diff % 3600) / 60);
        const remainingSeconds = diff % 60;
        setTimeLeft(
          `${days}d ${remainingHours}h ${remainingMinutes}m ${remainingSeconds
            .toString()
            .padStart(2, "0")}s`,
        );
      } else if (format === "compact") {
        const days = Math.floor(diff / 86400);
        const remainingHours = Math.floor((diff % 86400) / 3600);
        const remainingMinutes = Math.floor((diff % 3600) / 60);
        const remainingSeconds = diff % 60;

        // Show all non-zero units starting from the first non-zero unit
        const parts: string[] = [];
        
        if (days > 0) {
          parts.push(`${days}d`);
          if (remainingHours > 0) parts.push(`${remainingHours}h`);
          if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
          if (remainingSeconds > 0) parts.push(`${remainingSeconds.toString().padStart(2, "0")}s`);
        } else if (remainingHours > 0) {
          parts.push(`${remainingHours}h`);
          if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
          if (remainingSeconds > 0) parts.push(`${remainingSeconds.toString().padStart(2, "0")}s`);
        } else if (remainingMinutes > 0) {
          parts.push(`${remainingMinutes}m`);
          parts.push(`${remainingSeconds.toString().padStart(2, "0")}s`);
        } else {
          parts.push(`${remainingSeconds.toString().padStart(2, "0")}s`);
        }

        setTimeLeft(parts.join(" "));
      } else if (format === "compactNoSeconds") {
        const days = Math.floor(diff / 86400);
        const remainingHours = Math.floor((diff % 86400) / 3600);
        const remainingMinutes = Math.floor((diff % 3600) / 60);

        const parts: string[] = [];
        if (days > 0) {
          parts.push(`${days}d`);
          if (remainingHours > 0) parts.push(`${remainingHours}h`);
          if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
        } else if (remainingHours > 0) {
          parts.push(`${remainingHours}h`);
          if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
        } else if (remainingMinutes > 0) {
          parts.push(`${remainingMinutes}m`);
        } else {
          parts.push("0m");
        }
        setTimeLeft(parts.join(" "));
      } else {
        // Normal HH:MM:SS
        setTimeLeft(
          `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
        );
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [targetTimestamp, doneText, isTrenches, onComplete, format]);

  return timeLeft;
}
